// ============================================================================
// FSA NOTE MODAL — FORMULA EVALUATOR
// ============================================================================
// Small, safe arithmetic expression evaluator used by the disclosure grids
// to compute is_computed cells / rows.
//
// Supports:
//   + - * /  parentheses  numeric literals  identifiers (a-z, A-Z, _ digits)
//   dotted paths (foo.bar.baz) — useful for cross-disclosure references
//   unary minus
//
// Does NOT support function calls, exponents, modulo, comparisons, ternaries.
// Identifier resolution is delegated to a resolver function passed in by the
// caller — that's where the grid contexts plug in (per-row, per-column,
// per-disclosure).

type ResolverFn = (identifier: string) => number | undefined;

/**
 * Evaluate `formula` against a resolver. Returns `0` and logs to console
 * when the formula can't be parsed; returns `NaN` propagates through math.
 * Unknown identifiers resolve to `0` (matches Excel-style empty-cell behaviour).
 */
export function evaluateFormula(formula: string, resolve: ResolverFn): number {
  if (!formula || !formula.trim()) return 0;
  try {
    const tokens = tokenize(formula);
    const parser = new Parser(tokens, resolve);
    const result = parser.parseExpression();
    if (!parser.atEnd()) {
      // Trailing junk — formula likely malformed; surface a 0.
      // eslint-disable-next-line no-console
      console.warn('[formula] trailing tokens ignored:', formula);
    }
    return Number.isFinite(result) ? result : 0;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[formula] eval failed:', formula, err);
    return 0;
  }
}

// ───── tokenizer ────────────────────────────────────────────────────────────

type Token = { kind: 'num' | 'id' | 'op' | 'lparen' | 'rparen'; text: string };

function tokenize(formula: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  const n = formula.length;
  while (i < n) {
    const ch = formula[i];
    if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') { i++; continue; }
    if (ch === '(') { tokens.push({ kind: 'lparen', text: ch }); i++; continue; }
    if (ch === ')') { tokens.push({ kind: 'rparen', text: ch }); i++; continue; }
    if (ch === '+' || ch === '-' || ch === '*' || ch === '/') {
      tokens.push({ kind: 'op', text: ch });
      i++;
      continue;
    }
    if (isDigit(ch) || (ch === '.' && i + 1 < n && isDigit(formula[i + 1]))) {
      let j = i;
      let seenDot = false;
      while (j < n && (isDigit(formula[j]) || (formula[j] === '.' && !seenDot))) {
        if (formula[j] === '.') seenDot = true;
        j++;
      }
      tokens.push({ kind: 'num', text: formula.slice(i, j) });
      i = j;
      continue;
    }
    if (isIdStart(ch)) {
      let j = i;
      while (j < n && (isIdPart(formula[j]) || formula[j] === '.')) j++;
      tokens.push({ kind: 'id', text: formula.slice(i, j) });
      i = j;
      continue;
    }
    throw new Error(`Unexpected character '${ch}' at position ${i}`);
  }
  return tokens;
}

const isDigit = (c: string) => c >= '0' && c <= '9';
const isIdStart = (c: string) =>
  (c >= 'a' && c <= 'z') || (c >= 'A' && c <= 'Z') || c === '_';
const isIdPart = (c: string) => isIdStart(c) || isDigit(c);

// ───── recursive-descent parser ─────────────────────────────────────────────

class Parser {
  private pos = 0;
  constructor(private tokens: Token[], private resolve: ResolverFn) {}

  atEnd(): boolean { return this.pos >= this.tokens.length; }

  // expression := term ( ('+'|'-') term )*
  parseExpression(): number {
    let left = this.parseTerm();
    while (!this.atEnd() && this.tokens[this.pos].kind === 'op' &&
           (this.tokens[this.pos].text === '+' || this.tokens[this.pos].text === '-')) {
      const op = this.tokens[this.pos++].text;
      const right = this.parseTerm();
      left = op === '+' ? left + right : left - right;
    }
    return left;
  }

  // term := factor ( ('*'|'/') factor )*
  private parseTerm(): number {
    let left = this.parseFactor();
    while (!this.atEnd() && this.tokens[this.pos].kind === 'op' &&
           (this.tokens[this.pos].text === '*' || this.tokens[this.pos].text === '/')) {
      const op = this.tokens[this.pos++].text;
      const right = this.parseFactor();
      left = op === '*' ? left * right : left / right;
    }
    return left;
  }

  // factor := '-' factor | '(' expression ')' | num | id
  private parseFactor(): number {
    if (this.atEnd()) throw new Error('Unexpected end of formula');
    const tok = this.tokens[this.pos];
    if (tok.kind === 'op' && tok.text === '-') {
      this.pos++;
      return -this.parseFactor();
    }
    if (tok.kind === 'op' && tok.text === '+') {
      this.pos++;
      return this.parseFactor();
    }
    if (tok.kind === 'lparen') {
      this.pos++;
      const value = this.parseExpression();
      if (this.atEnd() || this.tokens[this.pos].kind !== 'rparen') {
        throw new Error('Missing closing parenthesis');
      }
      this.pos++;
      return value;
    }
    if (tok.kind === 'num') {
      this.pos++;
      return parseFloat(tok.text);
    }
    if (tok.kind === 'id') {
      this.pos++;
      const resolved = this.resolve(tok.text);
      return resolved ?? 0;
    }
    throw new Error(`Unexpected token '${tok.text}'`);
  }
}

// ───── row_key derivation (for fixed_grid formulas) ─────────────────────────

/**
 * Derive a stable row token from a row's `particulars` text (and an optional
 * explicit `row_key` override on the row). Strips common audit prefixes
 * ("Add:", "Less:") and lowercases, replacing non-alphanumeric runs with
 * underscores. Used to build the `row_<token>` identifiers referenced by
 * computed-row formulas (Closing balance = row_opening + row_issued - …).
 *
 * Convention is overridable: if a row supplies `row_key`, use that verbatim.
 */
export function deriveRowKey(row: { row_key?: string; particulars?: string; category?: string }): string {
  if (row.row_key && typeof row.row_key === 'string' && row.row_key.trim()) {
    return row.row_key.trim();
  }
  const text = (row.particulars ?? row.category ?? '').toString();
  if (!text) return '';
  const stripped = text
    .replace(/^\s*(Add|Less)\s*:\s*/i, '')
    .toLowerCase();
  // Take the first 1–2 meaningful tokens (skipping articles).
  const words = stripped
    .split(/[^a-z0-9]+/)
    .filter(w => w.length > 0 && !['the', 'of', 'for', 'a', 'an', 'in', 'on', 'and', 'or', 'to', 'during', 'year'].includes(w));
  return words.slice(0, 2).join('_') || 'row';
}
